import { Box, Skeleton, Stack } from "@mui/material";

/**
 * Reusable skeleton loading component that displays placeholder content
 * while data is being fetched.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useQuery(...)
 * if (isLoading) return <SkeletonLoader count={5} type="card" />
 * ```
 */

type SkeletonLoaderProps = {
  /**
   * Number of skeleton items to display.
   * @default 3
   */
  count?: number;

  /**
   * Type of skeleton to display.
   * - 'card': Rectangular blocks (for card layouts)
   * - 'table': Table row format (height 40px)
   * - 'text': Text line format (height 20px)
   * @default 'card'
   */
  type?: "card" | "table" | "text";

  /**
   * Whether to display as individual items or in a stack.
   * @default true
   */
  stacked?: boolean;

  /**
   * Gap between skeleton items (in MUI spacing units).
   * @default 2
   */
  spacing?: number;
};

/**
 * SkeletonLoader - Display loading skeleton UI while fetching data
 *
 * Shows placeholder content using Material-UI Skeleton component.
 * Automatically handles layout based on type and count.
 */
export function SkeletonLoader({
  count = 3,
  type = "card",
  stacked = true,
  spacing = 2,
}: SkeletonLoaderProps) {
  const getHeight = () => {
    switch (type) {
      case "card":
        return 120;
      case "table":
        return 40;
      case "text":
        return 20;
      default:
        return 120;
    }
  };

  const getVariant = () => {
    return type === "text" ? "text" : "rectangular";
  };

  const items = Array.from({ length: count }).map((_, i) => (
    <Skeleton
      key={i}
      variant={getVariant()}
      height={getHeight()}
      animation="wave"
      sx={{
        borderRadius: 1,
      }}
    />
  ));

  if (stacked) {
    return <Stack spacing={spacing}>{items}</Stack>;
  }

  return <Box sx={{ display: "flex", flexDirection: "column", gap: spacing }}>{items}</Box>;
}
